import React, { useEffect, useState } from 'react';
import styles from './CompanyInformations.module.sass';
import Card from '../../../../components/Card';
import TextInput from '../../../../components/TextInput';
import Dropdown from '../../../../components/Dropdown';
import Switch from '../../../../components/Switch';


// Options pour les menus déroulants
const SECTOR_OPTIONS = [
    { value: 'Technologies & Digital', title: 'Technologies & Digital' },
    { value: 'Finance & Assurance', title: 'Finance & Assurance' },
    { value: 'Santé & Pharmaceutique', title: 'Santé & Pharmaceutique' },
    { value: 'Éducation & Formation', title: 'Éducation & Formation' },
    { value: 'Commerce & Distribution', title: 'Commerce & Distribution' },
    { value: 'Industrie & Manufacturing', title: 'Industrie & Manufacturing' },
    { value: 'Services aux entreprises', title: 'Services aux entreprises' },
    { value: 'Transport & Logistique', title: 'Transport & Logistique' },
    { value: 'Énergie & Environnement', title: 'Énergie & Environnement' },
    { value: 'BTP & Construction', title: 'BTP & Construction' },
    { value: 'Agroalimentaire', title: 'Agroalimentaire' },
    { value: 'Médias & Communication', title: 'Médias & Communication' },
    { value: 'Tourisme & Hôtellerie', title: 'Tourisme & Hôtellerie' },
    { value: 'Autre', title: 'Autre' }
];

const COMPANY_TYPE_OPTIONS = [
    { value: 'TPE', title: 'TPE' },
    { value: 'PME', title: 'PME' },
    { value: 'ETI', title: 'ETI' },
    { value: 'Grande entreprise', title: 'Grande entreprise' },
    { value: 'Start-up', title: 'Start-up' },
    { value: 'Association', title: 'Association' },
    { value: 'Organisme public', title: 'Organisme public' },
    { value: 'Autre', title: 'Autre' }
];

// Options pour les types d'employeur séparées par catégorie
const TYPE_EMPLOYEUR_PRIVE_OPTIONS = [
    { value: 11, title: "11 : Entreprise inscrite au répertoire des métiers ou au registre des entreprises pour l'Alsace-Moselle" },
    { value: 12, title: "12 : Entreprise inscrite uniquement au registre du commerce et des sociétés" },
    { value: 13, title: "13 : Entreprises dont les salariés relèvent de la mutualité sociale agricole" },
    { value: 14, title: "14 : Profession libérale" },
    { value: 15, title: "15 : Association" },
    { value: 16, title: "16 : Autre employeur privé" },
];

const TYPE_EMPLOYEUR_PUBLIC_OPTIONS = [
    { value: 21, title: "21 : Service de l'Etat (administrations centrales et leurs services déconcentrés de la fonction publique d'Etat)" },
    { value: 22, title: "22 : Commune" },
    { value: 23, title: "23 : Département" },
    { value: 24, title: "24 : Région" },
    { value: 25, title: "25 : Etablissement public hospitalier" },
    { value: 26, title: "26 : Etablissement public local d'enseignement" },
    { value: 27, title: "27 : Etablissement public administratif de l'Etat" },
    { value: 28, title: "28 : Etablissement public administratif local(y compris établissement public de coopération intercommunale EPCI)" },
    { value: 29, title: "29 : Autre employeur public" }
];

// Options combinées pour l'initialisation
const TYPE_EMPLOYEUR_OPTIONS = [...TYPE_EMPLOYEUR_PRIVE_OPTIONS, ...TYPE_EMPLOYEUR_PUBLIC_OPTIONS];

const ORGANIZATION_TYPE_OPTIONS = [
    { value: 'Privé', title: 'Privé' },
    { value: 'Public', title: 'Public' }
];

const EMPLOYEUR_SPECIFIQUE_OPTIONS = [
    { value: 0, title: "0 : Aucun de ces cas" },
    { value: 1, title: "1 : Entreprise de travail temporaire" },
    { value: 2, title: "2 : Groupement d'employeurs" },
    { value: 3, title: "3 : Employeur saisonnier" },
    { value: 4, title: "4 : Apprentissage familial" }
];

// La fonction formatDateForInput a été supprimée car elle n'était pas utilisée

const CompanyInformations = ({ company, onCompanyChange, onDropdownChange, onSwitchChange }) => {
    const [filteredEmployerOptions, setFilteredEmployerOptions] = useState(TYPE_EMPLOYEUR_OPTIONS);
    
    // Déterminer automatiquement le type d'organisation en fonction du type d'employeur au chargement initial
    useEffect(() => {
        if (company?.type_of_employer && !company?.organization_type) {
            const employerType = Number(company.type_of_employer);
            let organizationType = null;
            
            // Si le type d'employeur est entre 11 et 16, c'est un employeur privé
            if (employerType >= 11 && employerType <= 16) {
                organizationType = 'Privé';
            }
            // Si le type d'employeur est entre 21 et 29, c'est un employeur public
            else if (employerType >= 21 && employerType <= 29) {
                organizationType = 'Public';
            }
            
            if (organizationType) {
                // Mettre à jour le type d'organisation dans l'objet company
                const event = {
                    target: {
                        name: 'organization_type',
                        value: organizationType
                    }
                };
                onCompanyChange(event);
            }
        }
    }, [company?.type_of_employer, company?.organization_type, onCompanyChange]);
    
    // Effet pour filtrer les options de type d'employeur en fonction du type d'organisation
    useEffect(() => {
        if (company?.organization_type === 'Privé') {
            setFilteredEmployerOptions(TYPE_EMPLOYEUR_PRIVE_OPTIONS);
        } else if (company?.organization_type === 'Public') {
            setFilteredEmployerOptions(TYPE_EMPLOYEUR_PUBLIC_OPTIONS);
        } else {
            setFilteredEmployerOptions(TYPE_EMPLOYEUR_OPTIONS);
        }
    }, [company?.organization_type]);
    
    return (
        <Card className={styles.card} title="Informations de l'entreprise" classTitle="title-green">
            <div className={styles.group}>
                <TextInput
                    className={styles.field}
                    label="Nom de l'entreprise"
                    name="name"
                    type="text"
                    placeholder="Nom de l'entreprise"
                    value={company?.name || ''}
                    onChange={onCompanyChange}
                    required
                />
                <TextInput
                    className={styles.field}
                    label="Dénomination sociale :"
                    name="corporate_name"
                    type="text"
                    placeholder="Dénomination sociale"
                    value={company?.corporate_name || ''}
                    onChange={onCompanyChange}
                    required
                />
                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Secteur d'activité :"
                        name="sector"
                        required
                        options={SECTOR_OPTIONS}
                        setValue={(option) => onDropdownChange('sector', option.value)}
                        value={SECTOR_OPTIONS.find(opt => opt.value === company?.sector) || ''}
                    />
                </div>
                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Type d'entreprise :"
                        name="type_of_company"
                        options={COMPANY_TYPE_OPTIONS}
                        setValue={(option) => onDropdownChange('type_of_company', option.value)}
                        value={COMPANY_TYPE_OPTIONS.find(opt => opt.value === company?.type_of_company) || ''}
                    />
                </div>
                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Type d'organisation :"
                        name="organization_type"
                        options={ORGANIZATION_TYPE_OPTIONS}
                        setValue={(option) => {
                            // Mettre à jour le type d'organisation
                            onDropdownChange('organization_type', option.value);
                            
                            // Réinitialiser le type d'employeur si le type d'organisation change
                            // pour éviter d'avoir un type d'employeur incompatible
                            if ((option.value === 'Privé' && Number(company?.type_of_employer) >= 21) ||
                                (option.value === 'Public' && Number(company?.type_of_employer) <= 16)) {
                                onDropdownChange('type_of_employer', null);
                            }
                        }}
                        value={ORGANIZATION_TYPE_OPTIONS.find(opt => opt.value === company?.organization_type) || ''}
                    />
                </div>
                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Type d'employeur :"
                        name="type_of_employer"
                        options={filteredEmployerOptions}
                        setValue={(option) => onDropdownChange('type_of_employer', option.value)}
                        value={TYPE_EMPLOYEUR_OPTIONS.find(opt => Number(opt.value) === Number(company?.type_of_employer)) || ''}
                    />
                </div>
                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Employeur spécifique :"
                        name="specific_employer"
                        options={EMPLOYEUR_SPECIFIQUE_OPTIONS}
                        setValue={(option) => onDropdownChange('specific_employer', option.value)}
                        value={EMPLOYEUR_SPECIFIQUE_OPTIONS.find(opt => Number(opt.value) === Number(company?.specific_employer)) || ''}
                    />
                </div>
                <TextInput
                    className={styles.field}
                    label="Nombre d'employés"
                    name="employee_count"
                    type="number"
                    placeholder="Nombre d'employés"
                    value={company?.employee_count || ''}
                    onChange={onCompanyChange}
                    required
                />
                <TextInput
                    className={styles.field}
                    label="Siret"
                    name="siret"
                    type="text"
                    placeholder="Siret"
                    value={company?.siret || ''}
                    onChange={onCompanyChange}
                />
                <TextInput
                    className={styles.field}
                    label="Code NAF / APE"
                    name="naf_code"
                    type="text"
                    placeholder="Code NAF"
                    value={company?.naf_code || ''}
                    onChange={onCompanyChange}
                />
                <TextInput
                    className={styles.field}
                    label="Code IDCC"
                    name="idcc_code"
                    type="text"
                    placeholder="Code IDCC"
                    value={company?.idcc_code || ''}
                    onChange={onCompanyChange}
                />
                <TextInput
                    className={styles.field}
                    label="Label IDCC"
                    name="Idcc_label"
                    type="text"
                    placeholder="Label IDCC"
                    value={company?.Idcc_label || ''}
                    onChange={onCompanyChange}
                />
                <TextInput
                    className={styles.field}
                    label="Caisse des retraites"
                    name="supplementary_pension_fund"
                    type="text"
                    placeholder="Caisse des retraites"
                    value={company?.supplementary_pension_fund || ''}
                    onChange={onCompanyChange}
                />

                <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Régime spécifique :</span>
                    <Switch
                        name="specific_diet"
                        value={company?.specific_diet || false}
                        onChange={(value) => onSwitchChange('specific_diet', value)}
                        className={styles.switch}
                    />
                </div>

            </div>
        </Card>
    );
};

export default CompanyInformations;